export interface LanguageOption {
  code: SupportedLanguageCode;
  label: string;
  locale: string;
  aliases: readonly string[];
}

export const SUPPORTED_LANGUAGES = [
  {
    code: 'en',
    label: 'English',
    locale: 'en-US',
    aliases: ['en', 'en-us', 'en-gb'],
  },
  {
    code: 'de',
    label: 'Deutsch',
    locale: 'de-DE',
    aliases: ['de', 'de-de', 'de-at', 'de-ch'],
  },
] as const;

export type SupportedLanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];

export const DEFAULT_LANGUAGE: SupportedLanguageCode = 'de';

const languageByAlias = new Map<string, SupportedLanguageCode>();

for (const language of SUPPORTED_LANGUAGES) {
  languageByAlias.set(language.code, language.code);
  languageByAlias.set(language.locale.toLowerCase(), language.code);
  for (const alias of language.aliases) {
    languageByAlias.set(alias.toLowerCase(), language.code);
  }
}

const normalizeLanguageTag = (value: string) => value.trim().toLowerCase().replace(/_/g, '-');

export const resolveSupportedLanguage = (
  value: string | null | undefined
): SupportedLanguageCode => {
  if (!value) {
    return DEFAULT_LANGUAGE;
  }

  const normalized = normalizeLanguageTag(value);
  const directMatch = languageByAlias.get(normalized);
  if (directMatch) {
    return directMatch;
  }

  const [baseLanguage] = normalized.split('-');
  return (baseLanguage ? languageByAlias.get(baseLanguage) : undefined) ?? DEFAULT_LANGUAGE;
};

export const getLanguageOption = (code: SupportedLanguageCode): LanguageOption =>
  SUPPORTED_LANGUAGES.find((language) => language.code === code) ?? SUPPORTED_LANGUAGES[0];

export const getLanguageLocale = (code: SupportedLanguageCode) => getLanguageOption(code).locale;
